// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiUrl } from '../utils/api-url';

/**
 * Service health states:
 * - healthy: API responding normally
 * - unavailable: API returning 502/503 or network error (likely deploy in progress)
 * - recovered: API was unavailable but has come back (transient — auto-clears)
 */
export type ServiceHealthStatus = 'healthy' | 'unavailable' | 'recovered';

const POLL_INTERVAL_MS = 15_000; // 15 seconds
const RECOVERY_DISPLAY_MS = 5_000; // show "recovered" banner for 5 seconds
const CONSECUTIVE_FAILURES_THRESHOLD = 2; // require 2 failures before showing banner

/**
 * Shared hook that polls /api/health to detect App Service unavailability.
 * Uses the existing [AllowAnonymous] health endpoint — no auth token needed.
 *
 * Shared across all GMS agent web apps.
 */
export function useServiceHealth() {
  const [status, setStatus] = useState<ServiceHealthStatus>('healthy');
  const consecutiveFailures = useRef(0);
  const wasUnavailable = useRef(false);
  const recoveryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkHealth = useCallback(async () => {
    try {
      // Direct fetch — no auth token needed for /api/health
      const response = await fetch(apiUrl('/api/health'), {
        method: 'GET',
        signal: AbortSignal.timeout(5_000),
      });

      if (response.ok) {
        consecutiveFailures.current = 0;

        if (wasUnavailable.current) {
          // Service just recovered
          wasUnavailable.current = false;
          setStatus('recovered');

          // Clear any existing recovery timer
          if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
          recoveryTimer.current = setTimeout(() => {
            setStatus('healthy');
            recoveryTimer.current = null;
          }, RECOVERY_DISPLAY_MS);
        }
        return;
      }

      // 502/503 = likely deploy in progress
      if (response.status === 502 || response.status === 503) {
        consecutiveFailures.current += 1;
        if (consecutiveFailures.current >= CONSECUTIVE_FAILURES_THRESHOLD) {
          wasUnavailable.current = true;
          setStatus('unavailable');
        }
      }
    } catch {
      // Network error (connection refused, timeout) — same as 502/503
      consecutiveFailures.current += 1;
      if (consecutiveFailures.current >= CONSECUTIVE_FAILURES_THRESHOLD) {
        wasUnavailable.current = true;
        setStatus('unavailable');
      }
    }
  }, []);

  useEffect(() => {
    // Initial check on mount
    void checkHealth();

    pollTimer.current = setInterval(() => void checkHealth(), POLL_INTERVAL_MS);

    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
      if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
    };
  }, [checkHealth]);

  return { status };
}
